import { CSSResultArray, TemplateResult, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './pc-card-carousel.css.lit';
import Splide from '@splidejs/splide';
import '../pc-container/pc-container';
import { OutlineElement } from '@phase2/outline-core';
import headingStyle from '../pc-heading/pc-heading.lightDom.css.lit';
import wysiwygStyle from '../pc-wysiwyg/pc-wysiwyg.lightDom.css.lit';
import linkStyle from '../pc-link/pc-link.lightDom.css.lit';
import iconStyle from '../pc-icon/pc-icon.css.lit';
import {AdoptedStyleSheets} from "@phase2/outline-adopted-stylesheets-controller";
import globalStyles from "../pc-news-card/pc-news-card.lightDom.css.lit";

@customElement('pc-card-carousel')
export class PCCardCarousel extends OutlineElement {
  static styles: CSSResultArray = [
    headingStyle,
    wysiwygStyle,
    linkStyle,
    iconStyle,
    componentStyles,
  ];

  _splide: Splide;

  @query('#splide')
  _splideRef: HTMLElement;

  // Selects only the default slot, excluding named slots like <slot name="cta"></slot>.
  // This ensures that we only interact with the default content slot.
  @query('slot:not([name])')
  _slot: HTMLSlotElement;

  @property()
  title: string;

  slides: TemplateResult[] = [];

  adoptedStylesheets: AdoptedStyleSheets;

  connectedCallback() {
    super.connectedCallback();

    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);
  }

  createReferenceSplide() {
    if (!this._splideRef) {
      throw new Error('Splide reference is null');
    }
    const oneSlide: boolean = this.slides.length > 1;

    this._splide = new Splide(this._splideRef, {
      speed: 300,
      perPage: oneSlide ? 3 : 2,
      focus: oneSlide ? undefined : 'center',
      arrows: oneSlide,
      gap: '2rem',
      padding: oneSlide ? { left: 0, right: '4rem' } : '1rem',
      pagination: false,
      breakpoints: {
        780: {
          perPage: 1,
          arrows: false,
        },
        1280: {
          perPage: oneSlide ? 2 : 2,
        },
      },
    }).mount();
  }

  firstUpdated() {
    if (!this._slot) {
      throw new Error('Slot is null');
    }
    this.slides = this._slot
      .assignedElements()
      .map(_ => html` <li class="splide__slide" role="">${_}</li> `);
    setTimeout(() => {
      this.createReferenceSplide();
      this.deleteAriaRoleFromSlide();
    }, 0);
    this.requestUpdate();
  }

  // This method addresses an accessibility issue reported by axe DevTools.
  // SplideJS adds a 'role="group"' attribute to slide list items that may not be appropriate,
  // which can cause accessibility concerns. This method removes the 'role' attribute
  // from each '.splide__slide' element to improve accessibility compliance.
  deleteAriaRoleFromSlide() {
    const listItems = this.renderRoot?.querySelectorAll('.splide__slide');
    if (listItems.length > 0)
      Array.from(listItems).forEach(listItem =>
        listItem.removeAttribute('role')
      );
  }

  render() {
    return html`
      <div class="splide" id="splide">
        ${this.title
          ? html`<div class="title">
              ${this.title} <slot name="cta"></slot>
            </div>`
          : null}

        <div class="splide__track">
          <ul class="splide__list ">
            <slot></slot>
            ${this.slides}
          </ul>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-card-carousel': PCCardCarousel;
  }
}
